import React, { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Search, User, Briefcase, List, 
  MapIcon, X, CheckCircle, Compass, Filter
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { toast } from "react-hot-toast";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom marker for single alumni
const createCustomIcon = (avatar) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="w-10 h-10 rounded-full border-2 border-white shadow-lg overflow-hidden bg-blue-500">
        ${avatar 
          ? `<img src="${avatar}" class="w-full h-full object-cover" />` 
          : `<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
              <span class="text-white text-sm font-semibold">A</span>
             </div>`
        }
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

// Custom marker for multiple alumni
const createMultiAlumniMarker = (count) => {
  return L.divIcon({
    className: 'custom-multi-marker',
    html: `
      <div class="w-12 h-12 rounded-full bg-blue-500 border-4 border-white shadow-lg 
                flex items-center justify-center text-white font-bold">
        ${count}
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 48],
    popupAnchor: [0, -48]
  });
};
const MapCenterControl = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && Array.isArray(center) && center.length === 2) {
      map.setView(center, 13);
    }
  }, [center, map]);
  
  return null;
};

const AlumniMap = ({ alumni }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([23.022505, 72.571362]); // Default to Ahmedabad
  const [radius, setRadius] = useState(50);
  const [viewMode, setViewMode] = useState("map");
  const [showFilters, setShowFilters] = useState(false);
  const searchRef = useRef(null);

  // Get current location on mount
  useEffect(() => {
    const requestLocation = () => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setMapCenter([latitude, longitude]);
            setSelectedLocation({
              coordinates: [latitude, longitude],
              name: "Current Location"
            });
          },
          (error) => {
            console.error("Error getting location:", error);
            toast.error("Unable to get your location. Using default location.");
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      } else {
        toast.error("Geolocation is not supported by your browser");
      }
    };

    requestLocation();
  }, []);

  // Filter valid alumni
  const validAlumni = useMemo(() => {
    return alumni.filter(alumnus => 
      alumnus?.personalInfo?.currentLocation?.coordinates?.[0] && 
      alumnus?.personalInfo?.currentLocation?.coordinates?.[1]
    );
  }, [alumni]);
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };
  // Filter by radius
  const filteredAlumni = useMemo(() => {
    if (!selectedLocation) return validAlumni;

    return validAlumni.filter(alumnus => {
      const [alumLng, alumLat] = alumnus.personalInfo.currentLocation.coordinates;
      const distance = calculateDistance(
        selectedLocation.coordinates[0],
        selectedLocation.coordinates[1],
        alumLat,
        alumLng
      );
      return distance <= radius;
    });
  }, [validAlumni, selectedLocation, radius]);

  // Group alumni by location
  const groupedAlumni = useMemo(() => {
    const groups = new Map();
    filteredAlumni.forEach(alumnus => {
      const [lng, lat] = alumnus.personalInfo.currentLocation.coordinates;
      const key = `${lat},${lng}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(alumnus);
    });
    return groups;
  }, [filteredAlumni]);

  // Handle search input
  const handleSearch = async (value) => {
    setSearchQuery(value);
    
    if (!value.trim() || value.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5`
      );
      
      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const formattedResults = data.map(place => ({
        id: place.place_id,
        name: place.display_name,
        coordinates: [parseFloat(place.lat), parseFloat(place.lon)]
      }));

      setSearchResults(formattedResults);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Location search failed');
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle location selection
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setSearchQuery(location.name);
    setMapCenter(location.coordinates);
    setShowSearchResults(false);
  };

  // Get current location
  const getCurrentLocation = () => {
    if ("geolocation" in navigator) {
      toast.loading('Getting your location...');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = {
            coordinates: [latitude, longitude],
            name: "Current Location"
          };
          setSelectedLocation(newLocation);
          setMapCenter([latitude, longitude]);
          toast.dismiss();
          toast.success('Location updated');
        },
        (error) => {
          console.error("Location error:", error);
          toast.dismiss();
          toast.error("Could not get your location");
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser");
    }
  };


  return (
    <div className="h-[100dvh] bg-gray-50 flex flex-col overflow-hidden">
      {/* Header with search controls */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="p-4">
          {/* Title and view toggles */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Alumni Map</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("map")}
                className={`p-2 rounded-md ${viewMode === "map" ? "bg-blue-100 text-blue-600" : "text-gray-500 hover:bg-gray-100"}`}
              >
                <MapIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md ${viewMode === "list" ? "bg-blue-100 text-blue-600" : "text-gray-500 hover:bg-gray-100"}`}
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search bar and location controls */}
          <div className="relative flex items-center gap-2" ref={searchRef}>
            <button
              onClick={getCurrentLocation}
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              title="Get current location"
            >
              <Compass className="h-5 w-5 text-gray-500" />
            </button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                placeholder="Search location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedLocation(null);
                    setSearchResults([]);
                    setShowSearchResults(false);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 border border-gray-300 rounded-md hover:bg-gray-50 ${showFilters ? 'bg-blue-50 border-blue-200' : ''}`}
            >
              <Filter className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Search results dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                  onClick={() => handleLocationSelect(result)}
                >
                  <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 truncate">{result.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Radius filter */}
          {showFilters && (
            <div className="mt-2 p-3 bg-gray-50 rounded-md">
              <select
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="5">5 km radius</option>
                <option value="10">10 km radius</option>
                <option value="25">25 km radius</option>
                <option value="50">50 km radius</option>
                <option value="100">100 km radius</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Map View */}
      <div className="flex-1 relative">
        <div className={`absolute inset-0 ${viewMode === "map" ? "block" : "hidden md:block"}`}>
          <MapContainer
            center={mapCenter}
            zoom={13}
            className="h-full w-full"
            ref={mapRef}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapCenterControl center={mapCenter} />
            
            {Array.from(groupedAlumni.entries()).map(([locationKey, alumniGroup]) => {
              const [lat, lng] = locationKey.split(',').map(Number);
              return (
                <Marker
                  key={locationKey}
                  position={[lat, lng]}
                  icon={alumniGroup.length > 1 
                    ? createMultiAlumniMarker(alumniGroup.length)
                    : createCustomIcon(alumniGroup[0].personalInfo?.avatar)
                  }
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      {alumniGroup.map((alumnus) => (
                        <div 
                          key={alumnus._id}
                          className="p-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/alumni/${alumnus._id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full overflow-hidden bg-gray-100">
                              {alumnus.personalInfo?.avatar ? (
                                <img
                                  src={alumnus.personalInfo.avatar}
                                  alt={alumnus.firstName}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <User className="h-5 w-5 text-gray-400 m-auto" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {alumnus.firstName} {alumnus.lastName}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {alumnus.department} ({alumnus.graduationYear})
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* List View */}
        <div className={`absolute inset-0 bg-gray-50 overflow-auto ${
          viewMode === "list" ? "block" : "hidden md:block md:w-96 md:left-auto"
        }`}>
          <div className="p-4 space-y-4">
            {filteredAlumni.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Alumni Found
                </h3>

                  <p className="text-gray-500 mb-4">
                    No alumni found within {radius}km of selected location
                  </p>
                  <button
                    onClick={() => {
                      setSelectedLocation(null);
                      setSearchQuery('');
                    }}
                    className="text-blue-500 hover:text-blue-600"
                  >
                    Clear location filter
                  </button>
                </div>
              ) : (
                filteredAlumni.map((alumnus) => (
                  <div
                    key={alumnus._id}
                    onClick={() => navigate(`/alumni/${alumnus._id}`)}
                    className="bg-white p-4 rounded-lg shadow-sm hover:shadow cursor-pointer transition-all"
                  >
                    <div className="flex gap-4">
                      <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100">
                        {alumnus.personalInfo?.avatar ? (
                          <img
                            src={alumnus.personalInfo.avatar}
                            alt={alumnus.firstName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="h-6 w-6 text-gray-400 m-auto" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {alumnus.firstName} {alumnus.lastName}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {alumnus.department} ({alumnus.graduationYear})
                        </p>
                        {alumnus.professionalInfo?.experiences?.find(e => e.isCurrent) && (
                          <p className="text-sm text-gray-600 mt-1">
                            {alumnus.professionalInfo.experiences.find(e => e.isCurrent).title}
                            {" at "}{alumnus.professionalInfo.experiences.find(e => e.isCurrent).company}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {alumnus.personalInfo?.currentLocation?.address || "Location not specified"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Mobile View Toggle */}
        <div className="md:hidden fixed bottom-6 right-6 z-30">
          <button
            onClick={() => setViewMode(viewMode === "map" ? "list" : "map")}
            className="bg-blue-500 text-white p-3 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
          >
            {viewMode === "map" ? (
              <List className="h-6 w-6" />
            ) : (
              <MapIcon className="h-6 w-6" />
            )}
          </button>
        </div>

        <style>
          {`
            .custom-marker {
              background: transparent;
              border: none;
            }
            .custom-marker:hover {
              transform: scale(1.1);
              transition: transform 0.2s ease-in-out;
            }
            .custom-multi-marker {
              background: transparent;
              border: none;
            }
            .custom-multi-marker:hover {
              transform: scale(1.1);
              transition: transform 0.2s ease-in-out;
            }
            .leaflet-container {
              z-index: 0;
            }
            .leaflet-popup-content-wrapper {
              padding: 0;
              overflow: hidden;
              border-radius: 8px;
            }
            .leaflet-popup-content {
              margin: 0;
              max-height: 400px;
              overflow-y: auto;
            }
            .leaflet-popup-close-button {
              padding: 10px !important;
              color: #6B7280 !important;
            }
          `}
        </style>
      </div>
    );
};

export default AlumniMap;